/* eslint-disable react/no-unescaped-entities */
import { useState } from "react";
import { motion } from "framer-motion";
import { Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Warm-black / champagne cinematic lead capture.
 * Matches CinematicHomePage aesthetic — Fraunces headline, thin dividers,
 * hairline inputs on transparent backgrounds, single champagne pill submit.
 */
const CinematicLead = () => {
  const [formData, setFormData] = useState({
    name: "", email: "", phone: "", moveInDate: "",
    stayLength: "", guests: "", reason: "", message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };
  const handleSelectChange = (name, value) => {
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.stayLength) {
      toast.error("Please fill in required fields");
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${BACKEND_URL}/api/inquiries`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone || null,
        check_in: formData.moveInDate || "TBD",
        check_out: "Extended Stay",
        guests: parseInt(formData.guests) || 1,
        message: `Stay Length: ${formData.stayLength}\nReason: ${formData.reason}\n\n${formData.message}`,
      });
      setIsSubmitted(true);
      toast.success("Thank you — we'll be in touch within the hour.");
    } catch (err) {
      console.error(err);
      toast.error("Failed to send. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputCls =
    "h-12 rounded-none bg-transparent border-0 border-b border-white/25 focus:border-[#C4A570] text-[#F5F1EA] placeholder:text-white/35 px-0 focus-visible:ring-0 focus-visible:ring-offset-0";
  const labelCls = "chapter-label mb-3 block text-white/60";

  if (isSubmitted) {
    return (
      <section className="bg-[#0D0D0D] cinematic-grain py-32 md:py-40" data-testid="lead-capture-section">
        <div className="container-premium">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl mx-auto text-center"
          >
            <div className="flex items-center gap-4 justify-center mb-8">
              <span className="chapter-num">08</span>
              <span className="h-px w-12 bg-[#C4A570]/60" />
              <span className="chapter-label">Received</span>
            </div>
            <h3 className="cinematic-serif text-[#F5F1EA] text-4xl md:text-5xl leading-[0.98] mb-8">
              Thank you.{" "}
              <span className="cinematic-serif-italic">We'll be in touch shortly.</span>
            </h3>
            <p className="cinematic-body max-w-md mx-auto">
              Your inquiry has been received. Expect a response from the owner
              within the hour with availability and personalized pricing.
            </p>
            <button
              onClick={() => setIsSubmitted(false)}
              className="mt-10 inline-flex items-center gap-3 border border-[#C4A570] text-[#C4A570] hover:bg-[#C4A570] hover:text-[#0D0D0D] transition-colors duration-500 px-8 py-3.5 rounded-full text-[11px] uppercase tracking-[0.35em]"
            >
              Submit Another Inquiry
              <span className="w-3 h-px bg-current" />
            </button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section
      id="contact"
      className="bg-[#0D0D0D] cinematic-grain py-28 md:py-40 relative"
      data-testid="lead-capture-section"
    >
      <div className="container-premium relative">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-16 lg:gap-24">
          {/* Left column — editorial copy */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1 }}
          >
            <div className="flex items-center gap-4 mb-8">
              <span className="chapter-num">08</span>
              <span className="h-px w-12 bg-[#C4A570]/60" />
              <span className="chapter-label">Reservations</span>
            </div>
            <h2 className="cinematic-serif text-[#F5F1EA] text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
              Tell us your dates.{" "}
              <span className="cinematic-serif-italic">We'll do the rest.</span>
            </h2>
            <p className="cinematic-body mt-8 max-w-md">
              Direct-owner booking. No middleman, no service fees.
              Expect a personal reply — usually within the hour.
            </p>
            <ul className="mt-10 space-y-4">
              {[
                "No commitment required",
                "Personal reply within the hour",
                "Pricing tailored to your exact dates",
                "Direct-owner communication throughout",
              ].map((line) => (
                <li key={line} className="flex items-start gap-4 cinematic-body text-sm">
                  <span className="mt-2 w-4 h-px bg-[#C4A570] flex-shrink-0" />
                  <span>{line}</span>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Right column — hairline form */}
          <motion.form
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 1, delay: 0.15 }}
            onSubmit={handleSubmit}
            className="space-y-8"
          >
            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className={labelCls}>Name *</label>
                <Input
                  name="name" value={formData.name} onChange={handleInputChange}
                  placeholder="Your name" className={inputCls} required data-testid="form-name"
                />
              </div>
              <div>
                <label className={labelCls}>Email *</label>
                <Input
                  name="email" type="email" value={formData.email} onChange={handleInputChange}
                  placeholder="your@email.com" className={inputCls} required data-testid="form-email"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className={labelCls}>Phone</label>
                <Input
                  name="phone" type="tel" value={formData.phone} onChange={handleInputChange}
                  placeholder="(407) 000-0000" className={inputCls} data-testid="form-phone"
                />
              </div>
              <div>
                <label className={labelCls}>Move-in Date</label>
                <Input
                  name="moveInDate" type="date" value={formData.moveInDate} onChange={handleInputChange}
                  className={`${inputCls} [color-scheme:dark]`} data-testid="form-date"
                />
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-8">
              <div>
                <label className={labelCls}>Length of Stay *</label>
                <Select value={formData.stayLength} onValueChange={(v) => handleSelectChange("stayLength", v)}>
                  <SelectTrigger className={inputCls} data-testid="form-stay">
                    <SelectValue placeholder="Select duration" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0D0D0D] text-[#F5F1EA] border-[#C4A570]/30">
                    <SelectItem value="3-months">3 Months</SelectItem>
                    <SelectItem value="6-months">6 Months</SelectItem>
                    <SelectItem value="seasonal">Seasonal (Jan–Apr)</SelectItem>
                    <SelectItem value="flexible">Flexible / Not sure</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Guests</label>
                <Select value={formData.guests} onValueChange={(v) => handleSelectChange("guests", v)}>
                  <SelectTrigger className={inputCls} data-testid="form-guests">
                    <SelectValue placeholder="How many" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0D0D0D] text-[#F5F1EA] border-[#C4A570]/30">
                    {[1,2,3,4,5,6,7,8,9,10].map(n => (
                      <SelectItem key={n} value={n.toString()}>{n} {n === 1 ? "guest" : "guests"}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className={labelCls}>Reason for Stay</label>
              <Select value={formData.reason} onValueChange={(v) => handleSelectChange("reason", v)}>
                <SelectTrigger className={inputCls} data-testid="form-reason">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent className="bg-[#0D0D0D] text-[#F5F1EA] border-[#C4A570]/30">
                  <SelectItem value="corporate">Corporate relocation</SelectItem>
                  <SelectItem value="travel-nurse">Travel nurse assignment</SelectItem>
                  <SelectItem value="snowbird">Snowbird / seasonal</SelectItem>
                  <SelectItem value="insurance">Insurance displacement</SelectItem>
                  <SelectItem value="renovation">Home renovation</SelectItem>
                  <SelectItem value="remote-work">Remote work</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className={labelCls}>Additional details</label>
              <Textarea
                name="message" value={formData.message} onChange={handleInputChange}
                placeholder="Anything we should know about your stay?"
                className="min-h-[110px] rounded-none bg-transparent border-0 border-b border-white/25 focus:border-[#C4A570] text-[#F5F1EA] placeholder:text-white/35 px-0 focus-visible:ring-0 focus-visible:ring-offset-0 resize-none"
                data-testid="form-message"
              />
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              data-testid="form-submit"
              className="mt-6 inline-flex items-center gap-3 border border-[#C4A570] text-[#C4A570] hover:bg-[#C4A570] hover:text-[#0D0D0D] disabled:opacity-50 transition-colors duration-500 px-10 py-4 rounded-full text-[11px] uppercase tracking-[0.35em]"
            >
              {isSubmitting ? "Sending…" : (
                <>
                  <Send className="w-3.5 h-3.5" />
                  Request a Quote
                  <span className="w-3 h-px bg-current" />
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

export default CinematicLead;
